import { DataGrid } from '@mui/x-data-grid'
import translate from '../../../i18n/translate'
import { modalsSlice } from '../../../modules/store/reducers/modalsSlice'
import useStyles from './useStyles'
import { Button, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import { Box } from '@mui/system'
import { useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { useAppDispatch } from '../../../modules/store/hooks'
import { organizerApi } from '../../../modules/services/organizerService'
import { format } from 'date-fns'
import DeclineRequestActionModal from '../../Modals/DeclineRequestActionModal'
import ApproveRequestActionModal from '../../Modals/ApproveRequestActionModal'
import { useNavigate } from 'react-router-dom'

type Props = {
  organizerId?: number;
}

const TAKE_COUNT = 10;

const OrganizerRequestDataGrid = (props: Props) => {
  const { organizerId } = props

  const classes = useStyles()
  const navigate = useNavigate();
  const dispatch = useAppDispatch()
  const [count, setCount] = useState<number>(0)
  const [columns, setColumns] = useState<any[]>([])
  const [rows, setRows] = useState<any[]>([])
  const [selectedOrganizer, setSelectedOrganizer] = useState<any | null>(null)

  //#region API requests
  const [approveRequest] = organizerApi.useApproveOrganizerRequestMutation()
  const [declineRequest] = organizerApi.useDeclineOrganizerRequestMutation()
  const [organizerRequestListDataLazyFetch] = organizerApi.useLazyGetOrganizerRequestListQuery()
  const {
    refetch: organizerRequestListDataRefetch,
    data: organizerRequestListData,
    isSuccess: isOrganizersSuccess,
    isFetching: isOrganizersFetching
  } = organizerApi.useGetOrganizerRequestListQuery({ organizerId })

  const { toggleDeclineRequestActionModal, toggleApproveRequestActionModal } = modalsSlice.actions

  const handleOpenRejectRequestModal = (organizer: any) => {
    dispatch(toggleDeclineRequestActionModal());
    setSelectedOrganizer(organizer)
  };

  const handleOpenApproveRequestModal = (organizer: any) => {
    dispatch(toggleApproveRequestActionModal());
    setSelectedOrganizer(organizer)
  };

  const handleRejectRequest = async (id: number, text?: string | null) => {
    try {
      await declineRequest({ organizerId: id, text })
      
      setRows((prev) => prev.filter((r) => r.id !== id))
    } catch (error: any) {
      toast.error(error?.message)
    } finally {
      setSelectedOrganizer(null)
    }
  };

  const handleApproveRequest = async (id: number, data: any) => {
    try {
      await approveRequest({ organizerId: id, data })
      
      setRows((prev) => prev.filter((r) => r.id !== id))
    } catch (error: any) {
      toast.error(error?.message)
    } finally {
      setSelectedOrganizer(null)
    }
  };
  //#endregion

  //#region Handlers

  function onLoadMoreClick () {
    organizerRequestListDataLazyFetch({
      skip: rows.length,
      take: TAKE_COUNT,
    })
      .then((response: any) => {
        const result = rows.concat(response.data.list)

        if (rows.length + TAKE_COUNT > 80) {
          result.splice(0, TAKE_COUNT)
          toast.success(translate('hideRows'))
        }

        setRows(result)
        setCount(response.data.count)
      })
      .catch((errors: any) => console.error(errors))
  }

  //#endregion

  //#region UseEffect`s
  useEffect(() => {
    if (isOrganizersSuccess) {
      setRows(organizerRequestListData.list)

      setCount(organizerRequestListData.count)
    }
  }, [organizerRequestListData, isOrganizersSuccess, setCount])

  
  const showOrganizerInfo = (id: number) => {
    navigate(`/organizers/all?organizerId=${id}`)
  }

  useEffect(() => {
    const organizerInfoColumn = {
      field: 'organizerInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridEventTransactionListOrganizerInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.EventRequestInfoCell}>
            <Typography>
              <span>{translate('gridEventTransactionListOrganizerName')}:</span> {params.row.user.name} {params.row.user.surname}
            </Typography>
            <Typography>
              <span>{translate('gridEventTransactionListOrganizerPhone')}:</span> {params.row.user.phone}
            </Typography>
            <Box my={1.5} width={1}>
              <Button
                size='small'
                color='warning'
                fullWidth
                onClick={() => showOrganizerInfo(params.row.user.id)}
              >
                {translate('gridEventTransactionListShowOrganizerInfo')}
              </Button>
            </Box>
          </Box>
        )
      }
    }

    const requestInfoColumn = {
      field: 'requestInfo',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridOrganizerRequestListRequestInfo'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.EventRequestInfoCell}>
            {params.row.earlyWithdrawalRequestDate ? (
              <Typography>
                <span>{translate('gridOrganizerRequestListRequestDate')}:</span> {format(new Date(params.row.earlyWithdrawalRequestDate), 'dd/MM/yyyy HH:mm')}
              </Typography>
            ) : null}
            {params.row.earlyWithdrawalRequestMessage ? (
              <Typography>
                <span>{translate('gridOrganizerRequestListRequestMessage')}:</span><br />{params.row.earlyWithdrawalRequestMessage}
              </Typography>
            ) : null}
          </Box>
        )
      }
    }

    let selectedColumns = [
      organizerInfoColumn,
      requestInfoColumn,
    ]

    selectedColumns.push({
      field: 'actions',
      flex: 1,
      filtrable: false,
      sortable: true,
      headerName: translate('gridOrganizerRequestListActions'),
      renderCell: (params: any) => {
        return (
          <Box className={classes.EventRequestInfoCell}>
            <Box display='flex' flexDirection='column' gap={1}>
              <Button
                color='warning'
                onClick={() => handleOpenApproveRequestModal(params.row)}
                >
                {translate('gridOrganizerRequestListApproveRequest')}
              </Button>
              <Button
                color='warning'
                onClick={() => handleOpenRejectRequestModal(params.row)}
              >
                {translate('gridOrganizerRequestListDeclineRequest')}
              </Button>
            </Box>
          </Box>
        )
      }
    } as any)

    setColumns(selectedColumns.filter((c) => !!c))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  useEffect(() => {
    organizerRequestListDataRefetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizerId])
  //#endregion

  if (isOrganizersFetching)
    return (
      <Box
        sx={{
          height: '400px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <CircularProgress color='warning' />
      </Box>
    )

  return (
    <>
      {selectedOrganizer && (
        <DeclineRequestActionModal 
          organizerId={selectedOrganizer?.id} 
          onSubmit={handleRejectRequest}
          label={translate('declineRequest')}
          buttonText={translate('declineRequest')}
        />
      )}
      {selectedOrganizer && (
        <ApproveRequestActionModal 
          organizer={selectedOrganizer} 
          onSubmit={handleApproveRequest}
          label={translate('approveRequest')}
          buttonText={translate('approveRequest')}
        />
      )}
      <Toaster position='bottom-right' toastOptions={{ duration: 4000 }} />
      <div
        style={{
          top: '0',
          width: '100%',
        }}
      >
        <DataGrid
          sx={{
            backgroundColor: '#ede9e4',
            borderRadius: '0px',
            border: 'none',

            '& .MuiDataGrid-virtualScroller': {
              marginTop: '0 !important'
            },
            '& .MuiDataGrid-main': {
              overflow: 'visible'
            },
            '.MuiDataGrid-columnHeaders': {
              backgroundColor: '#fc7f03',
              position: 'sticky',
              zIndex: 2,
              borderRadius: '0px',
              border: '0px'
            },
            '.MuiDataGrid-columnHeaderTitleContainer': {
              color: '#fff',
              fontSize: '15px',
              padding: '10px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center'
            },
            '.MuiDataGrid-cell': {
              border: '1px solid #fc7f03',
              padding: '5px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center'
            },
            '.MuiDataGrid-footerContainer': {
              border: '1px solid #fc7f03'
            },
            '.MuiDataGrid-footerContainer p': {
              margin: '0px'
            },
            '.MuiDataGrid-columnHeaderTitle': {
              overflow: 'visible',
              lineHeight: '1.43rem',
              whiteSpace: 'normal'
            }
          }}
          pageSize={rows.length}
          rows={rows}
          columns={columns}
          getRowHeight={() => 'auto'}
          disableSelectionOnClick
          autoHeight
          density='comfortable'
          headerHeight={70}
          hideFooter
        />
        <Button
          variant='contained'
          color='warning'
          style={{
            borderRadius: '0px',
            boxShadow: 'none'
          }}
          disabled={!(rows.length < count)}
          fullWidth
          onClick={() => onLoadMoreClick()}
          size='large'
        >
          {translate('customDataGridLoadMore')}
        </Button>
      </div>
    </>
  )
}

export default OrganizerRequestDataGrid
